import { useRuntimeConfig } from '#app'
import md5 from 'crypto-js/md5'
import { stringify as useStringify } from 'qs'

export const useAuthStore = defineStore('authStore', {
  persist: {
    pick: ['user', 'token']
  },
  state: () => ({
    user: null,
    token: null,
    enums: null,
    error: null,
    emailSent: true,
    loadingMe: false,
    loadingData: false,
    loadingLogin: false,
    loadingCreate: false,
    loadingUpdate: false,
    loadingCountry: false,
    loadingPayment: false,
    loadingResetPassword: false,
    loadingResetPasswordConfirm: false,
    loadingResendConfirmEmail: false,
    loadingConfirmEmail: false,
    loadingChangeEmailRequest: false,
    loadingChangeEmailConfirm: false,
    loadingUserCodeLookup: false
  }),
  actions: {
    async getEmailByUserCode(user_code) {
      this.loadingUserCodeLookup = true
      this.error = null
      const config = useRuntimeConfig()
      try {
        const response = await $fetch(`${config.public.strapiApi}/users-permissions/get-email-by-code`, {
          method: 'POST',
          body: { user_code }
        })
        this.loadingUserCodeLookup = false
        return response.email
      } catch (error) {
        console.error(error)
        this.error =
          error?.response?.data?.error?.message ||
          error?.data?.error?.message ||
          error?.message ||
          'Failed to retrieve email from user code'
        this.loadingUserCodeLookup = false
        throw error
      }
    },
    async login({ identifier, password }) {
      this.loadingLogin = true
      this.error = null
      const config = useRuntimeConfig()
      try {
        const hashed = md5(password).toString()
        const responseLogin = await $fetch(`${config.public.strapiApi}/auth/local`, {
          method: 'POST',
          body: { identifier, password: hashed }
        })

        // Check if the response contains an error (even if request didn't throw)
        if (responseLogin.error) {
          this.error = responseLogin.error.message || 'Authentication failed'
          this.loadingLogin = false
          return
        }

        // Check if we have valid auth data
        if (!responseLogin.jwt || !responseLogin.user) {
          this.error = 'Invalid authentication response'
          this.loadingLogin = false
          return
        }

        this.token = responseLogin.jwt
        this.user = responseLogin.user // Set user BEFORE calling me() so user_quality is available
        await this.me()
        this.loadingLogin = false
      } catch (error) {
        console.error(error)
        this.error =
          error?.response?.data?.error?.message ||
          error?.response?.error?.message ||
          error?.data?.error?.message ||
          'Authentication failed'
        this.loadingLogin = false
      }
    },
    checkAuthentication() {
      try {
        return !!(this.token && this.user)
      } catch (error) {
        console.error('Authentication check failed:', error)
        return false
      }
    },
    logout() {
      this.token = null
      this.user = null
      const authDataStore = useAuthDataStore()
      authDataStore.clearData()
      const consentStore = useConsentStore()
      consentStore.resetConsent()
    },
    async me() {
      this.loadingMe = true
      this.error = null
      const config = useRuntimeConfig()

      // Clear existing auth data before fetching new data to prevent stale data issues
      const authDataStore = useAuthDataStore()
      authDataStore.clearData()

      // Conditionally populate based on user_quality
      // A user can ONLY be one of: individuel (user_data), collectif (bar), or firme (firm)
      const userQuality = this.user?.user_quality

      let populateConfig = {}

      if (userQuality === 'collectif') {
        // Collective user - populate bar only
        populateConfig = {
          bar: {
            populate: [
              'directory_address',
              'directory_address.country',
              'directory_address.city',
              'contact_address',
              'contact_address.country',
              'contact_address.city',
              'billing_address',
              'billing_address.country',
              'billing_address.city',
              'bar_representatives'
            ]
          }
        }
      } else if (userQuality === 'firme') {
        // Firm user - populate firm only
        populateConfig = {
          firm: {
            populate: [
              'logo',
              'directory_address',
              'directory_address.country',
              'directory_address.city',
              'contact_address',
              'contact_address.country',
              'contact_address.city',
              'billing_address',
              'billing_address.country',
              'billing_address.city',
              'firm_representatives',
              'firm_representatives.photo'
            ]
          }
        }
      } else {
        // Individual user (default) - populate user_data only
        populateConfig = {
          user_data: {
            populate: [
              'photo',
              'directory_address',
              'directory_address.country',
              'directory_address.city',
              'billing_address',
              'billing_address.country',
              'billing_address.city',
              'contact_address',
              'contact_address.country',
              'contact_address.city'
            ]
          }
        }
      }

      const query = useStringify({ populate: populateConfig }, { encodeValuesOnly: true })

      try {
        const responseUser = await $fetch(`${config.public.strapiApi}/users/me?${query}`, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${this.token}`
          }
        })

        const authDataStore = useAuthDataStore()

        // Extract and store data based on user quality - only ONE will be present
        const userData = responseUser.user_data || null
        const barData = responseUser.bar || null
        const firmData = responseUser.firm || null

        authDataStore.setData(userData, barData, firmData)

        // Clean up responseUser before storing
        delete responseUser.user_data
        delete responseUser.bar
        delete responseUser.firm

        this.user = responseUser
        this.loadingMe = false
      } catch (error) {
        console.error(error?.response)
        console.error(error?.response?._data)
        this.error =
          error?.response?._data?.error?.message || error?.response?._data?.error || 'Failed to fetch user data'
        this.loadingMe = false
      }
    },
    async update(data) {
      this.loadingUpdate = true
      this.error = null
      const config = useRuntimeConfig()
      const authDataStore = useAuthDataStore()
      try {
        await $fetch(`${config.public.strapiApi}/users-data/update-user`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${this.token}`
          },
          body: {
            user: {
              documentId: this.user.documentId,
              userDataDocumentId: authDataStore.documentId,
              sugar_id: authDataStore.sugarId
            },
            data
          }
        })
        await this.me()
        this.loadingUpdate = false
      } catch (error) {
        console.error(error?.response)
        console.error(error?.response?._data)
        this.error = error?.response?._data?.error?.message || error?.response?._data?.error || 'Update failed'
        this.loadingUpdate = false
      }
    },
    async updateCountry(data) {
      this.loadingCountry = true
      this.error = null
      const config = useRuntimeConfig()
      const authDataStore = useAuthDataStore()
      try {
        const body = {
          user: {
            documentId: authDataStore.documentId,
            sugar_id: authDataStore.sugarId
          },
          ...data
        }
        const response = await $fetch(`${config.public.strapiApi}/users-data/update-country`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${this.token}`
          },
          body
        })
        await this.me()
        this.loadingCountry = false
        return response
      } catch (err) {
        this.loadingCountry = false
        console.error(err)
      }
    },
    async updateBilling(data) {
      this.loadingUpdate = true
      this.error = null
      const config = useRuntimeConfig()
      const authDataStore = useAuthDataStore()
      try {
        await $fetch(`${config.public.strapiApi}/users-data/update-billing`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${this.token}`
          },
          body: {
            user: {
              documentId: this.user.documentId,
              userDataDocumentId: authDataStore.documentId,
              sugar_id: authDataStore.sugarId
            },
            data
          }
        })
        await this.me()
        this.loadingUpdate = false
      } catch (error) {
        console.error(error?.response)
        console.error(error?.response?._data)
        this.error = error?.response?._data?.error?.message || error?.response?._data?.error || 'Billing update failed'
        this.loadingUpdate = false
        throw error
      }
    },
    async updateBar(data) {
      this.loadingUpdate = true
      this.error = null
      const config = useRuntimeConfig()
      const authDataStore = useAuthDataStore()
      try {
        await $fetch(`${config.public.strapiApi}/bars/update-bar`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${this.token}`
          },
          body: {
            user: {
              documentId: this.user.documentId,
              sugar_id: this.user.sugar_id
            },
            bar: {
              documentId: authDataStore.documentId,
              sugar_id: authDataStore.sugarId
            },
            data
          }
        })
        await this.me()
        this.loadingUpdate = false
      } catch (error) {
        console.error(error?.response)
        console.error(error?.response?._data)
        this.error = error?.response?._data?.error?.message || error?.response?._data?.error || 'Bar update failed'
        this.loadingUpdate = false
      }
    },
    async updateFirm(data) {
      this.loadingUpdate = true
      this.error = null
      const config = useRuntimeConfig()
      const authDataStore = useAuthDataStore()
      try {
        await $fetch(`${config.public.strapiApi}/firms/update-firm`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${this.token}`
          },
          body: {
            user: {
              documentId: this.user.documentId,
              sugar_id: this.user.sugar_id
            },
            firm: {
              documentId: authDataStore.documentId,
              sugar_id: authDataStore.sugarId
            },
            data
          }
        })
        await this.me()
        this.loadingUpdate = false
      } catch (error) {
        console.error(error?.response)
        console.error(error?.response?._data)
        this.error = error?.response?._data?.error?.message || error?.response?._data?.error || 'Firm update failed'
        this.loadingUpdate = false
      }
    },
    async uploadFirmLogo(file) {
      this.loadingUpdate = true
      this.error = null
      const config = useRuntimeConfig()
      const authDataStore = useAuthDataStore()
      try {
        const uploadedFile = await this.uploadFile(file)
        if (!uploadedFile) throw new Error('File upload failed')

        await $fetch(`${config.public.strapiApi}/firms/upload-logo`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${this.token}`
          },
          body: {
            user: {
              documentId: this.user.documentId,
              sugar_id: this.user.sugar_id
            },
            firm: {
              documentId: authDataStore.documentId,
              sugar_id: authDataStore.sugarId
            },
            data: {
              logo: uploadedFile.id
            }
          }
        })

        await this.me()
        this.loadingUpdate = false
        return uploadedFile
      } catch (error) {
        console.error(error?.response)
        console.error(error?.response?._data)
        this.error = error?.response?._data?.error?.message || error?.response?._data?.error || 'Logo upload failed'
        this.loadingUpdate = false
        throw error
      }
    },
    async uploadUserFile(file, documentType, existingIds = null) {
      this.loadingUpdate = true
      this.error = null
      const config = useRuntimeConfig()
      const authDataStore = useAuthDataStore()

      try {
        // 1. Upload file to Strapi
        const uploadedFile = await this.uploadFile(file)
        if (!uploadedFile) throw new Error('File upload failed')

        // 2. Prepare payload
        let fieldValue = uploadedFile.id
        if (Array.isArray(existingIds)) {
          fieldValue = [...existingIds, uploadedFile.id]
        }

        const body = {
          user: {
            documentId: this.user.documentId,
            userDataDocumentId: authDataStore.documentId,
            sugar_id: authDataStore.sugarId
          },
          data: {
            [documentType]: fieldValue,
            [`${documentType}_url`]: uploadedFile.full_url,
            [`${documentType}_name`]: uploadedFile.name
          }
        }

        const response = await $fetch(`${config.public.strapiApi}/users-data/upload-files`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${this.token}`
          },
          body
        })

        await this.me()
        this.loadingUpdate = false
        return response
      } catch (error) {
        console.error(error?.response)
        console.error(error?.response?._data)
        this.error = error?.response?._data?.error?.message || error?.response?._data?.error || 'Upload failed'
        this.loadingUpdate = false
        throw error
      }
    },
    async uploadFile(file) {
      try {
        const formData = new FormData()
        const config = useRuntimeConfig()
        formData.append('files', file)
        const request = await fetch(`${config.public.strapiApi}/upload`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${this.token}`
          },
          body: formData
        })
        const data = await request.json()
        const result = data[0]
        result.full_url = `${config.public.strapi}${data[0].url}`
        return result
      } catch (error) {
        console.error(error)
      }
    },
    async create(user) {
      this.loadingCreate = true
      this.error = null
      const config = useRuntimeConfig()
      const hashed = md5(user.password).toString()
      user.password = hashed
      try {
        const responseCreate = await $fetch(`${config.public.strapiApi}/users-permissions/create-user`, {
          method: 'POST',
          body: user
        })
        this.token = responseCreate.jwt
        this.emailSent = responseCreate.emailSent !== false
        this.loadingCreate = false
      } catch (error) {
        console.error(error?.response)
        console.error(error?.response?._data)
        this.error = error?.response?._data?.error?.message || error?.response?._data?.error || 'Registration failed'
        this.loadingCreate = false
      }
    },
    async getEnums() {
      const config = useRuntimeConfig()
      try {
        this.enums = null
        const response = await $fetch(`${config.public.strapiApi}/users-permissions/get-enums`, {
          method: 'GET'
        })
        this.enums = response
      } catch (error) {
        console.error(error?.response)
      }
    },
    async getTransactions(options = {}) {
      this.loadingData = true
      this.error = null
      const config = useRuntimeConfig()
      const authDataStore = useAuthDataStore()
      try {
        const queryParams = {
          userSugarId: authDataStore.sugarId,
          getDocuments: true
        }

        // Add pagination options
        if (options.offset !== undefined) {
          queryParams.offset = options.offset
        }

        if (options.maxNum !== undefined) {
          queryParams.maxNum = options.maxNum
        }

        if (options.orderBy) {
          queryParams.orderBy = options.orderBy
        }

        const query = useStringify(queryParams, { encodeValuesOnly: true })

        const response = await $fetch(`${config.public.strapiApi}/users-data/get-transactions?${query}`, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${this.token}`
          }
        })

        this.loadingData = false

        // Return the full response to include pagination info
        return {
          records: response.records || [],
          next_offset: response.next_offset,
          pagination: response.pagination
        }
      } catch (error) {
        console.error(error?.response)
        console.error(error?.response?._data)
        this.error =
          error?.response?._data?.error?.message || error?.response?._data?.error || 'Failed to fetch invoices'
        this.loadingData = false
        throw error
      }
    },
    async getTransactionBalances() {
      this.loadingData = true
      this.error = null
      const config = useRuntimeConfig()
      const authDataStore = useAuthDataStore()
      try {
        const queryParams = {
          userSugarId: authDataStore.sugarId
        }

        const query = useStringify(queryParams, { encodeValuesOnly: true })

        const response = await $fetch(`${config.public.strapiApi}/users-data/get-transaction-balances?${query}`, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${this.token}`
          }
        })

        this.loadingData = false

        return {
          data: response.data || [],
          meta: response.meta || {}
        }
      } catch (error) {
        console.error(error?.response)
        console.error(error?.response?._data)
        this.error =
          error?.response?._data?.error?.message ||
          error?.response?._data?.error ||
          'Failed to fetch transaction balances'
        this.loadingData = false
        throw error
      }
    },
    /**
     * Trustworthy alternative to SugarCRM's paid_c flag (which false-positives).
     * @param {*} eventRegistrationId
     * @param {*} eventId
     * @return {Promise<object>}
     */
    async getEventPaymentStatus(eventRegistrationId = null, eventId = null) {
      const config = useRuntimeConfig()
      const authDataStore = useAuthDataStore()
      const queryParams = {
        userSugarId: authDataStore.sugarId
      }

      if (eventRegistrationId) queryParams.eventRegistrationId = eventRegistrationId
      if (eventId) queryParams.eventId = eventId

      const query = useStringify(queryParams, { encodeValuesOnly: true })

      const response = await $fetch(`${config.public.strapiApi}/users-data/get-event-payment-status?${query}`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${this.token}`
        }
      })

      return {
        settled: response?.settled ?? false,
        hasDebit: response?.hasDebit ?? false,
        balance: response?.balance ?? 0
      }
    },
    async initiatePayment(amount = null, transactionReferences = null, eventId = null, currentLocale = 'fr') {
      const config = useRuntimeConfig()
      const authDataStore = useAuthDataStore()

      this.loadingPayment = true
      this.error = null

      try {
        // Map locale to payment provider format
        const localeMap = {
          fr: { language: 'fr', locale: 'fr_FR', countryCode: 'FR' },
          en: { language: 'en', locale: 'en_GB', countryCode: 'GB' },
          es: { language: 'es', locale: 'es_ES', countryCode: 'ES' }
        }
        const localeSettings = localeMap[currentLocale] || localeMap.fr

        const body = {
          userDocumentId: this.user.documentId,
          userSugarId: authDataStore.sugarId,
          language: localeSettings.language,
          locale: localeSettings.locale,
          countryCode: localeSettings.countryCode,
          ...(eventId && { eventId }),
          ...(amount && { amount }),
          ...(transactionReferences && { transactionRefs: transactionReferences })
        }

        const response = await $fetch(`${config.public.strapiApi}/payment/initiate`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${this.token}`,
            'Content-Type': 'application/json'
          },
          body
        })

        this.loadingPayment = false

        return response
      } catch (error) {
        console.error('Payment initiation error:', error)
        console.error('Error data:', error?.data)
        console.error('Error message:', error?.data?.error?.message)

        this.error = error?.data?.error?.message || error?.data?.error || error.message
        this.loadingPayment = false

        throw error
      }
    },
    async checkPaymentStatus(hostedCheckoutId) {
      const config = useRuntimeConfig()

      try {
        const response = await $fetch(`${config.public.strapiApi}/payment/checkout-status/${hostedCheckoutId}`, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${this.token}`
          }
        })

        return response
      } catch (error) {
        console.error('Error checking payment status:', error)

        throw error
      }
    },
    async requestPasswordReset(email) {
      this.loadingResetPassword = true
      this.error = null
      const config = useRuntimeConfig()
      try {
        const response = await $fetch(`${config.public.strapiApi}/users-permissions/forgot-password`, {
          method: 'POST',
          body: { email }
        })
        this.loadingResetPassword = false
        return response
      } catch (error) {
        console.error(error?.response)
        console.error(error?.response?._data)
        this.error = error?.response?._data?.error?.message || error?.response?._data?.error
        this.loadingResetPassword = false
        throw error
      }
    },
    async resetPassword({ code, password, passwordConfirmation }) {
      this.loadingResetPasswordConfirm = true
      this.error = null
      const config = useRuntimeConfig()
      try {
        const hashed = md5(password).toString()
        const hashedConfirmation = md5(passwordConfirmation).toString()
        // const response = await $fetch(`${config.public.strapiApi}/auth/reset-password`, {
        const response = await $fetch(`${config.public.strapiApi}/users-permissions/reset-password`, {
          method: 'POST',
          body: {
            code,
            password: hashed,
            passwordConfirmation: hashedConfirmation
          }
        })
        this.token = response.jwt
        this.user = response.user
        await this.me()
        this.loadingResetPasswordConfirm = false
        return response
      } catch (error) {
        console.error(error?.response)
        console.error(error?.response?._data)
        this.error = error?.response?._data?.error?.message || error?.response?._data?.error
        this.loadingResetPasswordConfirm = false
        throw error
      }
    },
    async resendConfirmEmail(email) {
      this.loadingResendConfirmEmail = true
      this.error = null
      const config = useRuntimeConfig()
      try {
        const response = await $fetch(`${config.public.strapiApi}/auth/send-confirm-email`, {
          method: 'POST',
          body: { email }
        })
        this.loadingResendConfirmEmail = false
        return response
      } catch (error) {
        console.error(error?.response)
        console.error(error?.response?._data)
        this.error = error?.response?._data?.error?.message || error?.response?._data?.error
        this.loadingResendConfirmEmail = false
        throw error
      }
    },
    async confirmEmail(token) {
      this.loadingConfirmEmail = true
      this.error = null
      const config = useRuntimeConfig()
      try {
        const response = await $fetch(`${config.public.strapiApi}/auth/confirm-email?confirmation=${token}`, {
          method: 'GET',
          redirect: 'manual' // Prevent following redirects to avoid CORS issues
        })

        // Check if response is successful or if it's a redirect (which indicates success)
        if (response && (response.jwt || response.user)) {
          // If we get jwt and user back, update the store
          if (response.jwt) {
            this.token = response.jwt
          }
          if (response.user) {
            this.user = response.user
            await this.me()
          }
        }

        this.loadingConfirmEmail = false
        return {
          jwt: response.jwt,
          user: response.user,
          message: response.message || 'Email confirmed successfully'
        }
      } catch (error) {
        console.error(error?.response)
        console.error(error?.response?._data)
        this.error = error?.response?._data?.error?.message || error?.response?._data?.error
        this.loadingConfirmEmail = false
        throw error
      }
    },
    async requestChangeEmail(newEmail) {
      this.loadingChangeEmailRequest = true
      this.error = null
      const config = useRuntimeConfig()
      try {
        const response = await $fetch(`${config.public.strapiApi}/auth/change-email/request`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${this.token}` },
          body: { newEmail }
        })
        this.loadingChangeEmailRequest = false
        return response
      } catch (error) {
        console.error(error?.response?._data)
        this.error =
          error?.response?._data?.error?.message || error?.response?._data?.error || 'Email change request failed'
        this.loadingChangeEmailRequest = false
        throw error
      }
    },
    async confirmChangeEmail(token) {
      this.loadingChangeEmailConfirm = true
      this.error = null
      const config = useRuntimeConfig()
      try {
        const response = await $fetch(`${config.public.strapiApi}/auth/change-email/confirm?token=${token}`, {
          method: 'GET',
          redirect: 'manual'
        })
        if (response?.jwt) this.token = response.jwt
        if (response?.user) this.user = response.user
        if (response?.jwt || response?.user) await this.me()
        this.loadingChangeEmailConfirm = false
        return response
      } catch (error) {
        console.error(error?.response?._data)
        this.error =
          error?.response?._data?.error?.message || error?.response?._data?.error || 'Email change confirmation failed'
        this.loadingChangeEmailConfirm = false
        throw error
      }
    }
  }
})
